<template>
  <div v-if="uiStore.activePanel === 'text'" id="text_wrapper">
    <!-- バックドロップ -->
    <div class="text-modal-backdrop" @click="cancel" />

    <div class="text-modal-inner">
      <!-- ヘッダー -->
      <div class="text-modal-header">
        <h3>{{ textStore.isEditing ? '文字を編集' : '文字を追加' }}</h3>
        <button class="text-modal-close" @click="cancel">✕</button>
      </div>

      <!-- テキスト入力 -->
      <div class="text-modal-section">
        <div class="text-input-row">
          <input
            ref="inputRef"
            type="text"
            v-model="textStore.currentText"
            placeholder="テキストを入力"
            class="text-input-field"
            :disabled="textStore.lockContent"
          />
          <button v-if="textStore.currentText && !textStore.lockContent" class="text-clear-btn" @click="textStore.currentText = ''">✕</button>
        </div>
      </div>

      <!-- フォント選択 (2段グリッド) -->
      <div class="text-modal-section" v-if="!fontLocked">
        <p class="text-modal-label">フォント</p>
        <div class="font-grid">
          <button
            v-for="font in FONT_LIST"
            :key="font.id"
            class="font-btn"
            :class="{ active: selectedFontId === font.id }"
            :style="{ fontFamily: font.family, fontWeight: font.weight || 'normal' }"
            @click="onFontSelect(font.id)"
          >{{ font.label }}</button>
        </div>
      </div>

      <!-- カラーパレット (20色, 10×2) -->
      <div class="text-modal-section" v-if="!fontLocked">
        <p class="text-modal-label">文字色</p>
        <div class="color-grid">
          <button
            v-for="color in presetColors"
            :key="color"
            class="color-swatch"
            :style="{ backgroundColor: color }"
            :class="{ active: textStore.currentColor === color }"
            @click="textStore.currentColor = color"
          />
        </div>
      </div>

      <!-- 書き方向 -->
      <div class="text-modal-section" v-if="!fontLocked">
        <p class="text-modal-label">方向</p>
        <div class="direction-btns">
          <button
            :class="{ active: textStore.currentDirection === 'horizontal' }"
            @click="textStore.currentDirection = 'horizontal'"
          >横書き</button>
          <button
            :class="{ active: textStore.currentDirection === 'vertical' }"
            @click="textStore.currentDirection = 'vertical'"
          >縦書き</button>
        </div>
      </div>

      <!-- 追加/保存ボタン -->
      <button
        class="text-add-btn"
        :disabled="!textStore.currentText.trim() || textStore.lockContent"
        @click="confirm"
      >{{ textStore.isEditing ? '変更を保存' : 'テキストを追加' }}</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue'
import { useTextStore } from '../../stores/useTextStore'
import { useCanvasStore } from '../../stores/useCanvasStore'
import { useUiStore } from '../../stores/useUiStore'
import { FONT_LIST } from '../../utils/fontDefinitions'

const textStore = useTextStore()
const canvasStore = useCanvasStore()
const uiStore = useUiStore()
const inputRef = ref(null)

// パネルが開いたら自動フォーカス
watch(() => uiStore.activePanel, (panel) => {
  if (panel === 'text') {
    nextTick(() => inputRef.value?.focus())
  }
})

// プリセットカラー (20色, 10×2)
const presetColors = [
  // Row 1: 鮮やか系
  '#FF0000', '#FF6600', '#FFCC00', '#66CC00', '#00CC44',
  '#00CCCC', '#0099FF', '#0033FF', '#6600FF', '#CC00CC',
  // Row 2: パステル・ダーク系
  '#FF6699', '#FFAA66', '#FFFF66', '#CCFF66', '#99FFCC',
  '#CCFFFF', '#CCCCFF', '#663300', '#FFFFFF', '#000000',
]

const selectedFontId = computed(() => {
  const f = FONT_LIST.find(f => f.family === textStore.currentFont)
  return f?.id ?? FONT_LIST[0].id
})

// lockContent: 入力 + フォント系すべて禁止 / lockFont: フォント系のみ禁止
const fontLocked = computed(() => textStore.lockContent || textStore.lockFont)

function onFontSelect(id) {
  const font = FONT_LIST.find(f => f.id === id)
  if (font) {
    textStore.currentFont = font.family
    textStore.currentWeight = font.weight
  }
}

function confirm() {
  if (!textStore.currentText.trim()) return
  uiStore.addColorHistory(textStore.currentColor)

  const size = canvasStore.canvasSize
  const textData = {
    objectType: 'text',
    content: textStore.currentText,
    font: textStore.currentFont,
    weight: textStore.currentWeight,
    color: textStore.currentColor,
    fontSize: textStore.currentFontSize,
    direction: textStore.currentDirection,
    width: 200,
    height: 60,
    rotation: 0,
  }

  if (textStore.isEditing && textStore.editingObjectId) {
    canvasStore.updateObject(textStore.editingObjectId, textData)
  } else {
    canvasStore.addObject({ ...textData, x: canvasStore.labelCenterX, y: canvasStore.labelCenterY })
  }
  textStore.reset()
  uiStore.hidePanel()
}

function cancel() {
  textStore.reset()
  uiStore.hidePanel()
}
</script>
